import i18next from "i18next";
import querystring from "querystring";
import MetricsUtil from "./MetricsUtil";
import {API} from "aws-amplify";

export enum HTTP_METHOD {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    DELETE = 'DELETE'
}

/** Lowest HTTP status treated as the service failing rather than the request being wrong. */
const SERVER_ERROR = 500;

export class ApiError extends Error {
    public readonly apiErrorCode: number;

    constructor(apiErrorCode: number, errorMsg: string) {
        super(errorMsg);

        this.apiErrorCode = apiErrorCode;

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, ApiError.prototype);
    }
}

export default class ApiUtil {
    // @ts-ignore
    private static readonly default_api_base_path = "/api";
    private static readonly default_api_call_timeout = 30 * 1000;

    private static csrfToken: string = '';

    private static apiMetrics: MetricsUtil = MetricsUtil.getDefaultMetricUtil();

    private static getUrl(resource: string, method: HTTP_METHOD, payloads?: { [key: string]: any; }): string {
        if (method === HTTP_METHOD.GET) {
            return ApiUtil.default_api_base_path + '/' + resource + '?' + ApiUtil.getQueryString(payloads);
        } else {
            return ApiUtil.default_api_base_path + '/' + resource + '?' +
                ApiUtil.getQueryString(this.getPayloadsElementDeep(payloads, 'countryCode'));
        }
    }

    private static getPayloadsElementDeep(payloads?: { [key: string]: any; }, destKey?: string): object {
        if (!payloads) {
            return {}
        }
        for (let key in payloads) {
            if (key == destKey) {
                return {[key]: payloads[key]}
            }
            if (typeof payloads[key] == 'object') {
                let res = this.getPayloadsElementDeep(payloads[key], destKey)
                if (destKey && destKey in res) {
                    return res
                }
            }
        }
        return {}
    }

    private static getQueryString(payloads?: { [key: string]: any; }): string {
        if (!payloads) {
            payloads = {};
        }

        payloads['locale'] = i18next.language;

        return querystring.stringify(payloads);
    }

    private static callApi(
        resource: string,
        payloads?: { [key: string]: any; },
        method = HTTP_METHOD.GET,
        headers = {}
    ) {
        let timedAttemptMetrics = MetricsUtil.getTimedAttemptMetrics('apiCall');

        let url = ApiUtil.getUrl(resource, method, payloads);

        return ApiUtil.chooseMethod(method, url, payloads, headers)
            .then((response) => {
                timedAttemptMetrics.setSuccess();
                ApiUtil.apiMetrics.getChildActionPublisherForMethod(resource).publish(timedAttemptMetrics);
                return response.data;
            }).catch((error) => {
                // The Amplify/axios error shape stops here — callers get an ApiError carrying a
                // status they can branch on. Neither the status nor the service's message is
                // rewritten: a caller cannot tell "this resource is gone" from "the API broke"
                // if every failure arrives as 500 "failed to fetch data from api".
                // A network-level failure has no `response` at all, hence the fallback.
                const status: number = error.response?.status ?? SERVER_ERROR;
                ApiUtil.apiMetrics.publishCounter(resource, `error_${status}`);

                // A client error means the request was wrong, not that the service failed, so
                // only 5xx counts against the attempt's success rate.
                if (status >= SERVER_ERROR) {
                    timedAttemptMetrics.setFailure();
                    ApiUtil.apiMetrics.getChildActionPublisherForMethod(resource).publish(timedAttemptMetrics);
                }

                throw new ApiError(status, error.response?.data?.message || "failed to fetch data from api");
            })
    }

    public static injectMetrics(metrics: MetricsUtil): void {
        ApiUtil.apiMetrics = metrics;
    }

    public static callApiWithTimeout(
        resource: string,
        payloads?: { [key: string]: any; },
        timeout = ApiUtil.default_api_call_timeout,
        method = HTTP_METHOD.GET,
        headers = {}
    ) {
        return Promise.race([
            ApiUtil.callApi(resource, payloads, method, headers),
            new Promise((_, reject) =>
                setTimeout(() => {
                    reject(new ApiError(408, 'timeout to call api'))
                }, timeout)
            )
        ]);
    }

    public static postApiWithTimeout(
        resource: string,
        payloads?: { [key: string]: any; },
        timeout = ApiUtil.default_api_call_timeout,
        method = HTTP_METHOD.POST,
    ) {
        let headers = {
            'anti-csrftoken-a2z': ApiUtil.csrfToken,
            'content-type': 'application/json; charset=UTF-8'
        };

        return ApiUtil.callApiWithTimeout(resource, payloads, timeout, method, headers);
    }

    private static chooseMethod(method: HTTP_METHOD,
                                url: string,
                                payloads?: { [key: string]: any; },
                                headers = {}) {
        switch (method) {
            case HTTP_METHOD.POST:
                return API.post("FeeTechPortalLambda", url, {
                    headers: headers,
                    response: true,
                    body: payloads
                });
            case HTTP_METHOD.PUT:
                return API.put("FeeTechPortalLambda", url, {
                    headers: headers,
                    response: true,
                    body: payloads
                });
            case HTTP_METHOD.DELETE:
                return API.del("FeeTechPortalLambda", url, {
                    headers: headers,
                    response: true,
                });
            default:
                return API.get("FeeTechPortalLambda", url, {
                    headers: headers,
                    response: true,
                });
        }
    }

    public static putApiWithTimeout(
        resource: string,
        payloads?: { [key: string]: any; },
        timeout = ApiUtil.default_api_call_timeout,
    ) {
        let headers = {
            'anti-csrftoken-a2z': ApiUtil.csrfToken,
            'content-type': 'application/json; charset=UTF-8'
        };

        return ApiUtil.callApiWithTimeout(resource, payloads, timeout, HTTP_METHOD.PUT, headers);
    }

    public static deleteApiWithTimeout(
        resource: string,
        payloads?: { [key: string]: any; },
        timeout = ApiUtil.default_api_call_timeout,
    ) {
        let headers = {
            'anti-csrftoken-a2z': ApiUtil.csrfToken,
            'content-type': 'application/json; charset=UTF-8'
        };

        return ApiUtil.callApiWithTimeout(resource, payloads, timeout, HTTP_METHOD.DELETE, headers);
    }
}